import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { IdField } from '~/components/Fields';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { dicomViewerAtom } from '~/state/atoms/viewerAtoms';
import PropertiesPanel from './PropertiesPanel';

interface RegistrationPropertiesProps {
    registration: RigidRegistration;
    onDelete?: () => void;
}

export default function RegistrationProperties({
    registration,
    onDelete,
}: RegistrationPropertiesProps) {
    const intl = useIntl();
    const dash = '\u2014';
    const setDicomViewer = useSetRecoilState(dicomViewerAtom);
    const datasetWrapper = registration.dicomParent.datasetWrapper;
    const onViewDicom = useCallback(() => {
        setDicomViewer({
            dataset: datasetWrapper,
            title: registration.contentLabel || 'Registration',
        });
    }, [datasetWrapper, registration.contentLabel, setDicomViewer]);

    return (
        <PropertiesPanel onViewDicom={onViewDicom} onDelete={onDelete}>
            <LabelText>
                <FormattedMessage id="label" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>
                    {registration.contentLabel || dash}
                </SensitiveInfoTypography>
            </ValueText>

            <LabelText>
                <FormattedMessage id="registration.contentDescription" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>
                    {registration.contentDescription || dash}
                </SensitiveInfoTypography>
            </ValueText>

            <LabelText>
                <FormattedMessage id="registration.contentCreator" />
            </LabelText>
            <ValueText component="span">
                {registration.contentCreatorName || dash}
            </ValueText>

            <LabelText>
                <FormattedMessage id="registration.contentDate" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>
                    {registration.contentDate || dash}
                </SensitiveInfoTypography>
            </ValueText>

            <LabelText>
                <FormattedMessage id="approvalStatus" />
            </LabelText>
            <ValueText component="span">
                {registration.approvalStatus}
            </ValueText>

            <LabelText>
                {intl.formatMessage({ id: 'registration.sourceImage' })} FOR
            </LabelText>
            <IdField
                id={registration.sourceFrameOfReferenceUid}
                typographyProps={{ fontSize: 11 }}
            />

            <LabelText>
                {intl.formatMessage({ id: 'registration.targetImage' })} FOR
            </LabelText>
            <IdField
                id={registration.targetFrameOfReferenceUid}
                typographyProps={{ fontSize: 11 }}
            />

            <LabelText>
                {intl.formatMessage({ id: 'sopInstanceUid' })}
            </LabelText>
            <IdField
                id={registration.sopInstanceUid}
                typographyProps={{ fontSize: 11 }}
            />
        </PropertiesPanel>
    );
}
